import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, CheckCircle2, Loader2, Globe } from "lucide-react";
import SEO from "@/components/site/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { full_name: "", email: "", telephone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: null });
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Full name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.telephone.trim()) e.telephone = "Telephone is required";
    if (!form.subject.trim()) e.subject = "Subject is required";
    if (!form.message.trim()) e.message = "Message is required";
    else if (form.message.trim().length < 10) e.message = "Please write at least 10 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm(initial);
      toast.success(data.message || "Thank you for contacting OneDial Africa.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Contact OneDial Africa — Scitygate Telecom UK Ltd. 2nd Floor, Halifax House, 93–101 Bridge Street, Manchester M3 2GX, United Kingdom. Telephone 0161 989 4842."
        path="/contact-us"
      />

      <section className="bg-brand-ink text-white">
        <div className="container-page py-20 lg:py-28">
          <p className="label-eyebrow text-brand-green">Contact</p>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl max-w-3xl text-balance">
            Talk to the team behind OneDial Africa.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/75">
            We typically respond within one business day. For account-specific questions,
            please include your registered email and phone number.
          </p>
        </div>
      </section>

      <section className="container-page py-20 grid lg:grid-cols-12 gap-12">
        {/* Info column */}
        <aside className="lg:col-span-5 space-y-8">
          <div className="rounded-xl border border-brand-line bg-white p-8">
            <h2 className="font-display font-bold text-2xl text-brand-ink">Head Office</h2>
            <address className="not-italic mt-5 space-y-4 text-sm text-brand-ink">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-brand-green mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">OneDial Africa</div>
                  <div className="text-brand-muted">
                    2nd Floor, Halifax House<br />
                    93–101 Bridge Street<br />
                    Manchester, M3 2GX<br />
                    United Kingdom
                  </div>
                  <div className="text-xs text-brand-muted mt-2">
                    OneDial Africa is a trading name of<br />
                    Scitygate Telecom UK Ltd, registered in England &amp; Wales<br />
                    under Company No. <span data-testid="contact-company-number">16468736</span>.
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-brand-green" />
                <a className="font-semibold hover:text-brand-green" href="tel:+441619894842" data-testid="contact-phone">
                  0161 989 4842
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-brand-green" />
                <a className="font-semibold hover:text-brand-green" href="mailto:support@onedialafrica.com" data-testid="contact-email">
                  support@onedialafrica.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Globe className="h-5 w-5 text-brand-green" />
                <a className="font-semibold hover:text-brand-green" href="https://www.onedialafrica.com">
                  www.onedialafrica.com
                </a>
              </div>
            </address>
          </div>

          <div className="rounded-xl border border-brand-line bg-white p-8">
            <h3 className="font-display font-bold text-lg text-brand-ink">Office hours</h3>
            <p className="mt-3 text-sm text-brand-muted">
              Monday – Friday · 09:00 – 18:00 GMT<br />
              Saturday · 10:00 – 14:00 GMT<br />
              Sunday · Closed
            </p>
            <p className="mt-4 text-xs text-brand-muted">
              In-app support is available 24/7 for OneDial Africa customers.
            </p>
          </div>
        </aside>

        {/* Form column */}
        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid="contact-thank-you"
              className="rounded-2xl border border-brand-line bg-white p-10 lg:p-14"
            >
              <CheckCircle2 className="h-10 w-10 text-brand-green" />
              <h2 className="mt-5 font-display font-black text-3xl text-brand-ink">
                Thank you for contacting OneDial Africa.
              </h2>
              <p className="mt-3 text-brand-muted leading-relaxed">
                Our team will respond as soon as possible.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                data-testid="contact-send-another"
                className="mt-8 inline-flex items-center gap-2 rounded-md border border-brand-line px-5 py-3 text-sm font-semibold text-brand-ink hover:bg-brand-bg"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              noValidate
              className="rounded-2xl border border-brand-line bg-white p-8 lg:p-10 space-y-5"
            >
              <Field
                label="Full Name"
                name="full_name"
                value={form.full_name}
                onChange={onChange}
                error={errors.full_name}
                placeholder="e.g. Amina Okafor"
                testId="contact-input-name"
                required
              />
              <div className="grid sm:grid-cols-2 gap-5">
                <Field
                  label="Email Address"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  error={errors.email}
                  placeholder="you@example.com"
                  testId="contact-input-email"
                  required
                />
                <Field
                  label="Telephone Number"
                  name="telephone"
                  value={form.telephone}
                  onChange={onChange}
                  error={errors.telephone}
                  placeholder="+44 7700 000000"
                  testId="contact-input-phone"
                  required
                />
              </div>
              <Field
                label="Subject"
                name="subject"
                value={form.subject}
                onChange={onChange}
                error={errors.subject}
                placeholder="How can we help?"
                testId="contact-input-subject"
                required
              />
              <div>
                <label className="block text-xs uppercase tracking-[0.22em] font-semibold text-brand-ink mb-2">
                  Message <span className="text-brand-green">*</span>
                </label>
                <textarea
                  name="message"
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  placeholder="Tell us a bit more…"
                  data-testid="contact-input-message"
                  className={`w-full rounded-md border bg-white px-4 py-3 text-sm text-brand-ink placeholder:text-brand-muted/70 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition ${
                    errors.message ? "border-red-400" : "border-brand-line"
                  }`}
                />
                {errors.message && (
                  <p className="mt-1.5 text-xs text-red-500" data-testid="error-message">{errors.message}</p>
                )}
              </div>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <p className="text-xs text-brand-muted">
                  By submitting, you agree to our{" "}
                  <a href="/privacy-policy" className="underline hover:text-brand-ink">Privacy Policy</a>.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-green px-6 py-3 text-sm font-semibold text-white hover:bg-brand-greenHover disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, error, placeholder, type = "text", required, testId }) {
  return (
    <div>
      <label htmlFor={name} className="block text-xs uppercase tracking-[0.22em] font-semibold text-brand-ink mb-2">
        {label} {required && <span className="text-brand-green">*</span>}
      </label>
      <input
        id={name}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className={`w-full rounded-md border bg-white px-4 py-3 text-sm text-brand-ink placeholder:text-brand-muted/70 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition ${
          error ? "border-red-400" : "border-brand-line"
        }`}
      />
      {error && (
        <p className="mt-1.5 text-xs text-red-500" data-testid={`${testId}-error`}>{error}</p>
      )}
    </div>
  );
}
